import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
//
import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

type Props = {
  uploading: boolean;
  onFile: (file: File) => void;
  hint?: string;
};

export default function FileDrop({ uploading, onFile, hint }: Props) {
  const [fileName, setFileName] = useState('');

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      setFileName(acceptedFiles[0].name);
      onFile(acceptedFiles[0]);
    },
    [onFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
  });

  return (
    <Box
      {...getRootProps()}
      data-testid="file-drop"
      sx={{
        p: 4,
        borderRadius: 2,
        cursor: 'pointer',
        textAlign: 'center',
        border: (theme) => `2px dashed ${alpha(theme.palette.grey[500], 0.32)}`,
        bgcolor: (theme) => (isDragActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent'),
        '&:hover': { opacity: 0.8 },
      }}
    >
      <input {...getInputProps()} data-testid="file-drop-input" />
      <Stack spacing={1} alignItems="center">
        {uploading ? (
          <CircularProgress size={32} />
        ) : (
          <Iconify icon="solar:cloud-upload-bold" width={40} sx={{ color: 'text.secondary' }} />
        )}
        <Typography variant="subtitle1">
          {uploading ? 'Processing your file…' : fileName || 'Drop your report here or click to browse'}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {hint || 'CSV or XLSX, up to 20 MB'}
        </Typography>
      </Stack>
    </Box>
  );
}
